import styles from './index.module.css'

import { MdClose } from '@react-icons/all-files/md/MdClose'
import { RxCheckCircled } from '@react-icons/all-files/rx/RxCheckCircled'
import { RxExclamationTriangle } from '@react-icons/all-files/rx/RxExclamationTriangle'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import { RxLockClosed } from '@react-icons/all-files/rx/RxLockClosed'
import { RxMagnifyingGlass } from '@react-icons/all-files/rx/RxMagnifyingGlass'
import cn from 'clsx'
import React, { useCallback, useState } from 'react'

import useLocalStorage from 'hooks/use-local-storage'

import { Box, Callout, IconButton } from '..'

type Props = {
  type:
    | 'accent'
    | 'neutral'
    | 'error'
    | 'success'
    | 'warning'
    | 'info'
    | 'search'
    | 'feature'
    | 'warning-important'
  highContrast?: boolean
  size?: '1' | '2' | '3'
  variant?: React.ComponentProps<typeof Callout.Root>['variant']
  dismissible?: boolean
  dismissKey?: string
  children: React.ReactNode
  icon?: React.ReactNode
} & React.ComponentProps<typeof Callout.Root>

const COLOR_MAP: Record<
  Props['type'],
  React.ComponentProps<typeof Callout.Root>['color']
> = {
  accent: 'indigo',
  neutral: 'gray',
  error: 'red',
  success: 'green',
  warning: 'amber',
  'warning-important': 'amber',
  info: 'sky',
  search: 'sky',
  feature: 'purple',
}

const Icon = ({ type }: { type: Props['type'] }) => {
  switch (type) {
    case 'error':
      return <RxExclamationTriangle />
    case 'success':
      return <RxCheckCircled />
    case 'warning':
      return <RxLockClosed />
    case 'warning-important':
      return <RxExclamationTriangle />
    case 'search':
      return <RxMagnifyingGlass />
    case 'info':
    case 'accent':
    case 'neutral':
      return <RxInfoCircled />
    case 'feature':
      return null
    default:
      return <RxInfoCircled />
  }
}

// this should match deprecating Alert component constant as this component will be drop-in replacement for Alert component
const ALERT_DISMISSED = 'alert_dismissed_'

/**
 *
 * A callout component with configurable type, size, variant and content.
 * Wrapper around the Callout component from @radix-ui/themes that adheres to the Disco design system
 *
 * @param type - The type of the callout
 * @param highContrast - Whether the callout should be high contrast
 * @param size - The size of the callout
 * @param variant - The variant of the callout
 * @param children - The content of the callout
 * @param icon - Optional custom icon to override the default type-based icon
 * @returns The callout component
 */
const DiscoCallout = ({
  type,
  highContrast = false,
  size = '1',
  variant = 'soft',
  dismissible = false,
  dismissKey,
  children,
  icon,
  ...rest
}: Props) => {
  const [dismissedDate, setDismissedDate] = useLocalStorage<
    Date | null | undefined
  >(ALERT_DISMISSED + dismissKey, null)

  const [isDismissed, setIsDismissed] = useState<boolean>(
    Boolean(dismissedDate),
  )
  const handleDismiss = useCallback(() => {
    setIsDismissed(true)

    if (dismissKey) {
      setDismissedDate(new Date())
    }
  }, [dismissKey, setDismissedDate])

  if (isDismissed) return null

  return (
    <Callout.Root
      color={COLOR_MAP[type]}
      highContrast={highContrast}
      size={size}
      variant={variant}
      className={styles.callout}
      {...rest}
    >
      <Callout.Icon>{icon || <Icon type={type} />}</Callout.Icon>
      <Callout.Text>{children}</Callout.Text>
      {dismissible && (
        <Box
          className={cn(styles.closeButton)}
          p={(parseInt(size) + 2).toString()}
        >
          <IconButton
            variant="ghost"
            color={COLOR_MAP[type]}
            size={size}
            onClick={handleDismiss}
          >
            <MdClose size="20px" />
          </IconButton>
        </Box>
      )}
    </Callout.Root>
  )
}

export default DiscoCallout
